import { useState, useCallback, useRef, ReactNode, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import styles from './ProgressCharts.module.css';

interface ChartConfig {
  label: string;
  component: ReactNode;
}

interface ChartCarouselProps {
  charts: ChartConfig[];
  isLoading?: boolean;
}

export function ChartCarousel({ charts, isLoading }: ChartCarouselProps) {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [displayIndex, setDisplayIndex] = useState(0);
  const isResettingRef = useRef(false);

  const originalCount = charts.length;
  const chartLabels = useMemo(() => charts.map(c => c.label), [charts]);

  // Triple the charts for virtual infinite loop
  const tripledCharts = useMemo(
    () => [...charts, ...charts, ...charts],
    [charts]
  );
  const startIndex = originalCount; // Start at first slide of middle set

  // Update display index on every slide change
  const handleSlideChange = useCallback((swiper: SwiperType) => {
    if (isResettingRef.current) return;
    const realIndex = swiper.activeIndex % originalCount;
    setDisplayIndex(realIndex);
  }, [originalCount]);

  // Reset position after transition ends (so teleport is invisible)
  const handleTransitionEnd = useCallback((swiper: SwiperType) => {
    if (isResettingRef.current) return;

    // If we've scrolled into the first or last set, silently reset to middle
    const needsReset =
      swiper.activeIndex < originalCount || swiper.activeIndex >= originalCount * 2;

    if (needsReset) {
      isResettingRef.current = true;

      // Disable CSS transitions on slides during teleport to prevent pulsation
      const slides = swiper.el.querySelectorAll('.swiper-slide');
      slides.forEach((slide) => {
        (slide as HTMLElement).style.transition = 'none';
      });

      const newIndex =
        swiper.activeIndex < originalCount
          ? swiper.activeIndex + originalCount
          : swiper.activeIndex - originalCount;

      swiper.slideTo(newIndex, 0);

      // Force synchronous reflow - ensures browser has applied all styles
      void swiper.el.offsetHeight;

      // Double RAF: first one schedules after current frame, second after paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          slides.forEach((slide) => {
            (slide as HTMLElement).style.transition = '';
          });
          isResettingRef.current = false;
        });
      });
    }
  }, [originalCount]);

  const handleIndicatorClick = (index: number) => {
    if (swiperInstance) {
      // Calculate target in middle set
      const targetIndex = originalCount + index;
      swiperInstance.slideTo(targetIndex);
    }
  };

  return (
    <div className={`${styles.carouselContainer} ${isLoading ? styles.loading : ''}`}>
      <Swiper
        modules={[Navigation]}
        navigation
        centeredSlides
        slidesPerView={1.5}
        spaceBetween={16}
        initialSlide={startIndex}
        onSwiper={setSwiperInstance}
        onSlideChange={handleSlideChange}
        onSlideChangeTransitionEnd={handleTransitionEnd}
        className={styles.swiper}
      >
        {tripledCharts.map((chart, idx) => (
          <SwiperSlide key={`${chart.label}-${idx}`} className={styles.swiperSlide}>
            {chart.component}
          </SwiperSlide>
        ))}
      </Swiper>

      <div className={styles.indicators}>
        {chartLabels.map((label, index) => (
          <button
            key={label}
            className={`${styles.indicator} ${index === displayIndex ? styles.indicatorActive : ''}`}
            onClick={() => handleIndicatorClick(index)}
            aria-label={`Go to ${label} chart`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
